import { useMemo, useCallback } from 'react';
import { UseMutateFunction } from '@tanstack/react-query';
import { usePendingBudgetRequests } from './usePendingBudgetRequests';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { useBudgetRequestDetails } from './useBudgetRequestDetails';
import { useBudgetProposalConversation } from './useBudgetProposalConversation';
import { useBudgetRequestsByTaskId } from './useBudgetRequestsByTaskId';
import { useCreateBudgetRequest } from './useCreateBudgetRequest';
import { useRespondToBudgetRequest } from './useRespondToBudgetRequest';
import { useCreateCounterProposal } from './useCreateCounterProposal';
import { useAcceptBudgetProposal } from './useAcceptBudgetProposal';
import { useRejectBudgetProposal } from './useRejectBudgetProposal';
import { useAcceptBudgetRequest } from './useAcceptBudgetRequest';
import { useRejectBudgetRequest } from './useRejectBudgetRequest';
import { useCancelBudgetRequest } from './useCancelBudgetRequest';
import {
  BudgetRequestCreateInput,
  BudgetProposalCreateInput,
  BudgetCounterProposalCreateInput,
} from '@models/budget.model';
import { budgetService } from '@services/budget';

// Sprint 23, story-111 (s4fxo8Ga). Each of the 8 mutation wrappers below used to reject on
// failure, and every call site (budgets.tsx, BudgetDetailsModal/index.tsx) awaits them with no
// try/catch — an unhandled promise rejection on every failed budget mutation. The underlying
// mutation hooks already toast on error via their own onError (unchanged, untouched) — this
// wrapper resolves falsy instead of rejecting so the caller's own `if (await wrapper(...))`
// branch (every existing call site already has one, or is made to have one) does the right thing
// without a try/catch anywhere. No new toast, no change to WHEN or WHETHER a toast fires.
function asFalsyOnError<TData, TVariables>(
  mutate: UseMutateFunction<TData, unknown, TVariables>,
  input: TVariables,
): Promise<TData | false> {
  return new Promise((resolve) => {
    mutate(input, {
      onSuccess: (data) => resolve(data),
      onError: () => resolve(false),
    });
  });
}

export const useBudget = () => {
  const { organizationId } = useAppSelector(
    (state: RootState) => state.organization,
  );

  const pendingBudgetRequestsQuery = usePendingBudgetRequests(
    organizationId || undefined,
  );

  // Mutation hooks
  const createBudgetRequestMutation = useCreateBudgetRequest();
  const respondToBudgetRequestMutation = useRespondToBudgetRequest();
  const createCounterProposalMutation = useCreateCounterProposal();
  const acceptBudgetProposalMutation = useAcceptBudgetProposal();
  const rejectBudgetProposalMutation = useRejectBudgetProposal();
  const acceptBudgetRequestMutation = useAcceptBudgetRequest();
  const rejectBudgetRequestMutation = useRejectBudgetRequest();
  const cancelBudgetRequestMutation = useCancelBudgetRequest();

  // Count pending budgets (both sent and received that need attention)
  const countPendingBudgets = useMemo(() => {
    const pendingBudgetRequests = pendingBudgetRequestsQuery.data || [];
    return pendingBudgetRequests.length;
  }, [pendingBudgetRequestsQuery.data]);

  return {
    // Data - Handle errors gracefully by returning empty arrays
    pendingBudgetRequests: pendingBudgetRequestsQuery.error
      ? []
      : pendingBudgetRequestsQuery.data || [],
    budgetProposals: [], // This will be managed by individual components using useBudgetProposalConversation

    // Loading states
    loading:
      pendingBudgetRequestsQuery.isLoading ||
      createBudgetRequestMutation.isPending ||
      respondToBudgetRequestMutation.isPending ||
      createCounterProposalMutation.isPending ||
      acceptBudgetProposalMutation.isPending ||
      rejectBudgetProposalMutation.isPending ||
      acceptBudgetRequestMutation.isPending ||
      rejectBudgetRequestMutation.isPending ||
      cancelBudgetRequestMutation.isPending,

    // Error states
    error: pendingBudgetRequestsQuery.error,
    hasPermissionError:
      (pendingBudgetRequestsQuery.error as any)?.response?.errors?.[0]?.extensions?.code ===
      'FORBIDDEN',

    fetchPendingBudgetRequests: useCallback(async () => {
      return budgetService.getPendingBudgetRequests();
    }, []),

    fetchBudgetRequestDetails: useCallback(async (budgetRequestId: string) => {
      return budgetService.getBudgetRequestDetails(budgetRequestId);
    }, []),

    fetchBudgetProposalConversation: useCallback(
      async (budgetRequestId: string) => {
        return budgetService.getBudgetProposalConversation(budgetRequestId);
      },
      [],
    ),

    fetchBudgetRequestsByTaskId: useCallback(async (taskId: string) => {
      return budgetService.getBudgetRequestsByTaskId(taskId);
    }, []),

    // Individual query hooks for components that need them
    useBudgetRequestDetails,
    useBudgetProposalConversation,
    useBudgetRequestsByTaskId,

    // Mutation functions (return promises for backwards compatibility) — each resolves falsy on
    // failure (asFalsyOnError) rather than rejecting; see that function's comment above.
    createBudgetRequest: useCallback(
      (input: BudgetRequestCreateInput) => asFalsyOnError(createBudgetRequestMutation.mutate, input),
      [createBudgetRequestMutation],
    ),

    respondToBudgetRequest: useCallback(
      (input: BudgetProposalCreateInput) => asFalsyOnError(respondToBudgetRequestMutation.mutate, input),
      [respondToBudgetRequestMutation],
    ),

    createCounterProposal: useCallback(
      (input: BudgetCounterProposalCreateInput) => asFalsyOnError(createCounterProposalMutation.mutate, input),
      [createCounterProposalMutation],
    ),

    acceptBudgetProposal: useCallback(
      (proposalId: string) => asFalsyOnError(acceptBudgetProposalMutation.mutate, proposalId),
      [acceptBudgetProposalMutation],
    ),

    rejectBudgetProposal: useCallback(
      (proposalId: string) => asFalsyOnError(rejectBudgetProposalMutation.mutate, proposalId),
      [rejectBudgetProposalMutation],
    ),

    acceptBudgetRequest: useCallback(
      (budgetRequestId: string) => asFalsyOnError(acceptBudgetRequestMutation.mutate, budgetRequestId),
      [acceptBudgetRequestMutation],
    ),

    rejectBudgetRequest: useCallback(
      (budgetRequestId: string) => asFalsyOnError(rejectBudgetRequestMutation.mutate, budgetRequestId),
      [rejectBudgetRequestMutation],
    ),

    cancelBudgetRequest: useCallback(
      (budgetRequestId: string) => asFalsyOnError(cancelBudgetRequestMutation.mutate, budgetRequestId),
      [cancelBudgetRequestMutation],
    ),

    // Utility function
    countPendingBudgets,

    // Refetch functions
    refetch: useCallback(() => {
      pendingBudgetRequestsQuery.refetch();
    }, [pendingBudgetRequestsQuery]),
  };
};
